import {useEffect, useRef} from "react";

/**
 * Elements carrying this attribute (or nested inside one) never count as an outside click, no matter
 * which consumer of this hook is listening. Meant for interactive overlays portaled to document.body
 * (e.g. the source-tag tooltip) that logically belong to content rendered inside a popup.
 */
export const OUTSIDE_CLICK_IGNORE_ATTRIBUTE = 'data-outside-click-ignore';

/**
 * Reusable hook that checks whether a click has come from outside the given DOM container
 * @author Muhammad Shahrukh <shahrukh@250mils.com>
 * @param {React.Ref} containerToCheckRef the Ref that for the DOM element for which we need to check whether there has been a click outside it or not.
 *                           defaults to the document
 * @param {function} onClickedOutside callback to execute once a click outside has been registered
 * @param {Element|Array<Element|null>|null} excludeFromCheck dom element(s) outside the container that should be excluded from the check
 * @param {function|null} setHandlerCondition Optional. A function if true, sets the handler. returns otherwise.
 * @param {'mousedown'|'click'} eventType Optional. Which event dismisses. Defaults to 'mousedown', which dismisses as
 *        soon as the button goes down. Pass 'click' when the callback moves the page — collapsing a section, say. On
 *        mousedown the layout shifts while the button is still held, so whatever was under the cursor is gone by
 *        mouseup, and the browser then fires `click` on the nearest common ancestor of the two targets instead of on
 *        the element pressed: that element's onClick never runs. Waiting for 'click' keeps the press intact, and the
 *        same click still reaches this handler, so it dismisses too.
 * */
const useOutsideClick = (containerToCheckRef, onClickedOutside, excludeFromCheck = null, setHandlerCondition = null, eventType = 'mousedown') => {
    const shouldSetHandler = typeof setHandlerCondition === 'function' ? setHandlerCondition(): true;

    const excludeFromCheckRef = useRef(excludeFromCheck);
    excludeFromCheckRef.current = excludeFromCheck;

    /**
     *
     * @param {Event} e
     * @returns {boolean}
     */
    const isClickFromExcludedElement = (e) => {
        const excludeValue = excludeFromCheckRef.current;
        if (excludeValue === null) {
            return false;
        }
        const excludedElements = Array.isArray(excludeValue) ? excludeValue : [excludeValue];
        return excludedElements.some((element) => element !== null && element.contains(e.target));
    }

    /**
     *
     * @param {Event} e
     * @returns {boolean}
     */
    const isClickFromIgnoredElement = (e) => {
        return e.target instanceof Element && e.target.closest(`[${OUTSIDE_CLICK_IGNORE_ATTRIBUTE}]`) !== null;
    }

    /**
     *
     * @param {Event} e
     * @returns {boolean}
     */
    const isClickFromOutsideContainer = (e) => {
        if (containerToCheckRef.current) {
            return (!containerToCheckRef.current.contains(e.target) && !isClickFromExcludedElement(e) && !isClickFromIgnoredElement(e))
        }

        return false;
    }

    useEffect(() => {
        if (!shouldSetHandler) {
            return;
        }

        /**
         *
         * @param {Event} e
         */
        const handler = (e) => {
            if (isClickFromOutsideContainer(e) && typeof onClickedOutside === 'function') {
                onClickedOutside(e);
            }
        }

        document.addEventListener(eventType, handler, {capture: true});

        return () => {
            document.removeEventListener(eventType, handler, {capture: true});
        }
    }, [onClickedOutside, shouldSetHandler, eventType]);
}

export default useOutsideClick;